import { dayjs } from '@noor/lib';

/**
 * Live delivery-timeline countdown, ported VERBATIM from cpanel
 * `DeliveryTimeline.calculateCountdown`. Drives the arrival / waiting / search
 * badges: a value counting toward (or past) `endDate`, plus a status tone the
 * badge colors itself with.
 *
 * - `onlyShow` freezes the clock at `doneAt` (a finished leg); otherwise it
 *   counts against now and the UI re-renders it every second.
 * - The sign is cpanel's (`(!isNegative ? '-' : '+')`): a target still in the
 *   future reads `+mm:ss` (time left), an overrun reads `-mm:ss`.
 * - Hours come from `dayjs.duration().hours()` there — the 0–23 hour
 *   component, unpadded — and the tone from the whole-minute difference
 *   truncated toward zero (`dayjs.diff(…, 'minutes')`).
 */

export type CountdownStatus = 'success' | 'warning' | 'error';

export interface CountdownInput {
  /** ISO instant the leg is measured against (eta / arrival / search start). */
  endDate: string | null | undefined;
  /** ISO instant the leg finished; used as "now" when `onlyShow` is set. */
  doneAt?: string | null | undefined;
  /** Freeze at `doneAt` instead of ticking against the live clock. */
  onlyShow?: boolean | undefined;
}

export interface CountdownResult {
  /** Signed, formatted duration, e.g. `+12:30` (left) or `-1:05:42` (late). */
  time: string;
  status: CountdownStatus;
}

const LATE_MINUTES = 3;
const WARNING_MINUTES = -1;
/** `dayjs.duration().hours()` is the hour COMPONENT (wraps at a day). */
const HOURS_PER_DAY = 24;

/** Compute the signed countdown string + status tone for a timeline leg. */
export function calculateCountdown(input: CountdownInput): CountdownResult | null {
  if (!input.endDate) return null;
  const endTime = dayjs(input.endDate);
  if (!endTime.isValid()) return null;

  const nowTime = input.onlyShow ? dayjs(input.doneAt ?? undefined) : dayjs();
  if (!nowTime.isValid()) return null;

  const diffMs = nowTime.diff(endTime);
  const abs = Math.abs(diffMs);

  const totalSeconds = Math.floor(abs / 1000);
  const hours = Math.floor(totalSeconds / 3600) % HOURS_PER_DAY;
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, '0');
  const body =
    hours > 0
      ? `${hours}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(minutes)}:${pad(seconds)}`;

  const time = `${diffMs < 0 ? '+' : '-'}${body}`;

  const diffMinutes = nowTime.diff(endTime, 'minute');
  const status: CountdownStatus =
    diffMinutes > LATE_MINUTES
      ? 'error'
      : diffMinutes > WARNING_MINUTES
        ? 'warning'
        : 'success';

  return { time, status };
}
